import React, { FC, ReactNode, memo, useEffect, useRef } from 'react';
import { Box, CarouselWIP, CloseButton, Text } from '@wix/design-system';
import { FormattedMessage } from 'react-intl';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames';

import { MediaManagerSDKFile } from '../../../utils/media-manager-sdk';
import { useIntersectionObserver } from '../../../utils/hooks/useIntersectionObserver';
import { RelatedItemsSkeleton } from './RelatedItemsSkeleton/RelatedItemsSkeleton';
import { RelatedItemsErrorState } from './RelatedItemsErrorState/RelatedItemsErrorState';
import { RELATED_ITEMS_TRANSITION_TIMEOUT, RELATED_ITEM_HEIGHT } from './constants';

import styles from './RelatedItems.scss';

interface RelatedItemsProps {
  onClose: () => void;
  onRefresh: () => void;
  loadNextItems: () => void;
  renderFile(file: MediaManagerSDKFile): ReactNode;
  onOpened: (nodeRef) => void;
  relatedItems: MediaManagerSDKFile[];
  isLoading: boolean;
  hasNextRelatedItems: boolean;
  isError: boolean;
  isShown: boolean;
  showExitAnimation?: boolean;
}

const RelatedItemsComponent: FC<RelatedItemsProps> = ({
  onClose,
  renderFile,
  loadNextItems,
  onRefresh,
  relatedItems,
  isLoading,
  hasNextRelatedItems,
  isError,
  isShown,
  onOpened,
  showExitAnimation = true,
}) => {
  const [lastItemRef, entry] = useIntersectionObserver();
  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (entry?.isIntersecting && !isLoading && hasNextRelatedItems) {
      loadNextItems();
    }
  }, [entry, isLoading, loadNextItems, hasNextRelatedItems]);

  const calculateGridSize = (file: MediaManagerSDKFile) => {
    const formatWidth = file.width ?? RELATED_ITEM_HEIGHT;
    const formatHeight = file.height ?? RELATED_ITEM_HEIGHT;

    const ratio = formatWidth / formatHeight;

    return {
      height: RELATED_ITEM_HEIGHT,
      width: Math.floor(ratio * RELATED_ITEM_HEIGHT),
      ratio,
    };
  };

  const showSkeleton = !relatedItems.length && isLoading;
  const showRelatedItemsCarousel = !!relatedItems.length && !isError;

  const onTransitionEntered = () => {
    if (!nodeRef.current) {
      return;
    }
    onOpened(nodeRef.current);
  };

  return (
    <CSSTransition
      classNames={{
        appear: styles.slideAppear,
        appearActive: styles.slideAppearActive,
        appearDone: styles.slideAppearDone,
        enter: styles.slideEnter,
        enterActive: styles.slideEnterActive,
        enterDone: styles.slideEnterDone,
        exit: styles.slideExit,
        exitActive: styles.slideExitActive,
        exitDone: styles.slideExitDone,
      }}
      appear={isShown}
      in={isShown}
      nodeRef={nodeRef}
      timeout={{
        appear: RELATED_ITEMS_TRANSITION_TIMEOUT,
        exit: showExitAnimation ? RELATED_ITEMS_TRANSITION_TIMEOUT : 0,
        enter: RELATED_ITEMS_TRANSITION_TIMEOUT,
      }}
      unmountOnExit
      onEntered={onTransitionEntered}
    >
      <Box
        overflow="hidden"
        display="block"
        ref={nodeRef}
        className={classNames(styles.slideWrapper, {
          [styles.slideExitAnimation]: showExitAnimation,
        })}
      >
        <Box
          dataHook="related-items-wrapper"
          borderRadius={8}
          direction="vertical"
          className={styles.slideContent}
          backgroundColor="B60"
          gap="SP2"
          padding="SP2"
          marginBottom="SP2"
          marginLeft="1px"
          marginTop="1px"
          marginRight="1px"
        >
          <Box WebkitJustifyContent="space-between" width="100%">
            <Text size="medium">
              <FormattedMessage id="related.images.hover-and-title" />
            </Text>
            <CloseButton dataHook="related-items-close" skin="dark" size="medium" onClick={onClose} />
          </Box>

          {showSkeleton && <RelatedItemsSkeleton onClose={onClose} />}
          {showRelatedItemsCarousel && (
            <Box display="block">
              <CarouselWIP
                controlsStartEnd="hidden"
                infinite={false}
                controlsPosition="overlay"
                controlsSkin="transparent"
                variableWidth
                gutter={12}
                hideDots
                dataHook="related-items-carousel"
                sidesGradientColor="#fff"
              >
                {relatedItems.map((file, index) => (
                  <Box
                    key={file.id}
                    ref={index === relatedItems.length - 1 ? lastItemRef : null}
                    scrollSnapAlign="start"
                    dataHook="related-item"
                  >
                    {renderFile({ ...file, smartGridSize: calculateGridSize(file) })}
                  </Box>
                ))}
              </CarouselWIP>
            </Box>
          )}
          {isError && <RelatedItemsErrorState onRefresh={onRefresh} />}
        </Box>
      </Box>
    </CSSTransition>
  );
};

export const RelatedItems = memo(RelatedItemsComponent);
